'use client';

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  ArrowLeft,
  Search,
  Phone,
  MessageCircle,
  UserPlus,
  Upload,
  X,
  ChevronRight,
  Users,
  Check,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Contact {
  id: string;
  name: string;
  phone: string;
  initials: string;
  avatarGradient: string;
  isOnline?: boolean;
  status?: string;
  email?: string;
}

type ModalType = 'add' | 'import' | null;

interface AddContactForm {
  name: string;
  phone: string;
  status: string;
}

interface ContactsScreenProps {
  onBack: () => void;
  onMessage: () => void;
}

function getInitials(name: string): string {
  return name
    .trim()
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

const GRADIENTS = [
  'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)',
  'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
  'linear-gradient(135deg, #06B6D4 0%, #0284C7 100%)',
  'linear-gradient(135deg, #EC4899 0%, #BE185D 100%)',
  'linear-gradient(135deg, #10B981 0%, #059669 100%)',
  'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
  'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
  'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
  'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
  'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
];

export default function ContactsScreen({ onBack, onMessage }: ContactsScreenProps) {
  const { user } = useAuth();
  const supabase = createClient();

  const [query, setQuery] = useState('');
  const [modal, setModal] = useState<ModalType>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addForm, setAddForm] = useState<AddContactForm>({ name: '', phone: '', status: '' });
  const [addErrors, setAddErrors] = useState<Partial<AddContactForm>>({});
  const [addLoading, setAddLoading] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  const fetchContacts = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('user_profiles')
        .select('id, full_name, phone, avatar_gradient, is_online, status_message, email')
        .neq('id', user.id)
        .order('full_name', { ascending: true });

      if (fetchError) throw fetchError;

      const mapped: Contact[] = (data || []).map(row => ({
        id: row.id,
        name: row.full_name || row.email?.split('@')[0] || 'Unknown',
        phone: row.phone || '',
        initials: getInitials(row.full_name || row.email?.split('@')[0] || 'U'),
        avatarGradient: row.avatar_gradient || GRADIENTS[0],
        isOnline: row.is_online ?? false,
        status: row.status_message || '',
        email: row.email,
      }));

      setContacts(mapped);
    } catch (err: any) {
      setError(err?.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Real-time online status updates
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('user_profiles_online')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'user_profiles' },
        payload => {
          const updated = payload.new as any;
          setContacts(prev =>
            prev.map(c =>
              c.id === updated.id
                ? { ...c, isOnline: updated.is_online, status: updated.status_message }
                : c
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, supabase]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return contacts;
    return contacts.filter(
      c =>
        c.name.toLowerCase().includes(q) ||
        c.phone.includes(q) ||
        (c.email || '').toLowerCase().includes(q)
    );
  }, [query, contacts]);

  const grouped = useMemo(() => {
    const map: Record<string, Contact[]> = {};
    filtered.forEach(c => {
      const letter = c.name[0]?.toUpperCase() || '#';
      if (!map[letter]) map[letter] = [];
      map[letter].push(c);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const onlineCount = useMemo(() => contacts.filter(c => c.isOnline).length, [contacts]);

  const validateAdd = () => {
    const errs: Partial<AddContactForm> = {};
    if (!addForm.name.trim()) errs.name = 'Name is required';
    if (!addForm.phone.trim()) errs.phone = 'Phone number is required';
    else if (!/^\+?[\d\s\-()]{7,}$/.test(addForm.phone.trim()))
      errs.phone = 'Enter a valid phone number';
    return errs;
  };

  const handleAddContact = async () => {
    const errs = validateAdd();
    if (Object.keys(errs).length > 0) {
      setAddErrors(errs);
      return;
    }
    setAddLoading(true);
    try {
      // Optimistically add to local list
      const newContact: Contact = {
        id: `local-${Date.now()}`,
        name: addForm.name.trim(),
        phone: addForm.phone.trim(),
        initials: getInitials(addForm.name.trim()),
        avatarGradient: GRADIENTS[Math.floor(Math.random() * GRADIENTS.length)],
        status: addForm.status.trim() || 'Hey there!',
        isOnline: false,
      };
      setContacts(prev => [...prev, newContact]);
      setAddForm({ name: '', phone: '', status: '' });
      setAddErrors({});
      setModal(null);
    } finally {
      setAddLoading(false);
    }
  };

  const handleImport = () => {
    setImportSuccess(true);
    setTimeout(() => {
      setImportSuccess(false);
      setModal(null);
    }, 1800);
  };

  return (
    <div
      style={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: '#101C29',
      }}
    >
      {/* Header */}
      <header
        className="flex items-center gap-3 px-3 flex-shrink-0"
        style={{
          height: '64px',
          background: 'rgba(16,28,41,0.97)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <button
          onClick={onBack}
          className="flex items-center justify-center w-[40px] h-[40px] rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-all duration-150 flex-shrink-0"
          aria-label="Back"
        >
          <ArrowLeft size={22} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-[18px] font-bold text-white tracking-tight">Contacts</h1>
          <p className="text-[12px]" style={{ color: 'rgba(255,255,255,0.38)' }}>
            {loading ? 'Loading…' : `${contacts.length} contacts · ${onlineCount} online`}
          </p>
        </div>
        <button
          onClick={fetchContacts}
          className="flex items-center justify-center w-[40px] h-[40px] rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-all duration-150"
          aria-label="Refresh contacts"
        >
          <RefreshCw size={18} />
        </button>
        <button
          onClick={() => setModal('add')}
          className="flex items-center justify-center w-[40px] h-[40px] rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-all duration-150"
          aria-label="Add contact"
        >
          <UserPlus size={20} />
        </button>
      </header>

      {/* Search bar */}
      <div className="px-4 py-3 flex-shrink-0" style={{ background: '#101C29' }}>
        <div
          className="w-full flex items-center gap-2.5 px-3.5 rounded-2xl"
          style={{ background: 'rgba(255,255,255,0.07)', height: '40px' }}
        >
          <Search size={16} className="text-white/40 flex-shrink-0" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search contacts…"
            className="flex-1 bg-transparent outline-none text-[14px] text-white placeholder-white/35"
            aria-label="Search contacts"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-white/40 hover:text-white/70 transition-colors">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Quick actions row */}
      <div
        className="flex gap-3 px-4 pb-3 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <QuickActionBtn
          icon={<UserPlus size={18} />}
          label="Add Contact"
          gradient="linear-gradient(135deg, #287FD8 0%, #1A5FAA 100%)"
          onClick={() => setModal('add')}
        />
        <QuickActionBtn
          icon={<Upload size={18} />}
          label="Import"
          gradient="linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)"
          onClick={() => setModal('import')}
        />
        <QuickActionBtn
          icon={<Users size={18} />}
          label="Groups"
          gradient="linear-gradient(135deg, #22C55E 0%, #15803D 100%)"
          onClick={onMessage}
        />
      </div>

      {/* Online users strip */}
      {!loading && onlineCount > 0 && (
        <div
          className="flex-shrink-0 px-4 py-2.5"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
        >
          <p className="text-[11px] font-semibold mb-2" style={{ color: 'rgba(255,255,255,0.38)' }}>
            ONLINE NOW
          </p>
          <div className="flex gap-3 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {contacts
              .filter(c => c.isOnline)
              .map(c => (
                <button
                  key={c.id}
                  onClick={() => setSelectedContact(c)}
                  className="flex flex-col items-center gap-1 flex-shrink-0 transition-opacity hover:opacity-80"
                >
                  <div className="relative">
                    <div
                      className="w-[44px] h-[44px] rounded-full flex items-center justify-center text-white font-bold text-[13px]"
                      style={{ background: c.avatarGradient }}
                    >
                      {c.initials}
                    </div>
                    <span
                      className="absolute bottom-0 right-0 w-[11px] h-[11px] bg-green-400 rounded-full border-2"
                      style={{ borderColor: '#101C29' }}
                    />
                  </div>
                  <span className="text-[10px] text-white/50 max-w-[44px] truncate">{c.name.split(' ')[0]}</span>
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Contact list */}
      <div className="flex-1 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <Loader2 size={28} className="text-white/30 animate-spin" />
            <p className="text-[14px] text-white/40">Loading contacts…</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 px-8 text-center">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(239,68,68,0.1)' }}
            >
              <Users size={28} className="text-red-400/60" />
            </div>
            <p className="text-[15px] font-semibold text-white/60">Failed to load contacts</p>
            <p className="text-[13px] text-white/30">{error}</p>
            <button
              onClick={fetchContacts}
              className="mt-1 px-4 py-2 rounded-xl text-[13px] font-semibold text-white/70 transition-all hover:opacity-80"
              style={{ background: 'rgba(255,255,255,0.08)' }}
            >
              Try again
            </button>
          </div>
        ) : grouped.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 px-8 text-center">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.06)' }}
            >
              <Users size={28} className="text-white/30" />
            </div>
            <p className="text-[15px] font-semibold text-white/60">No contacts found</p>
            <p className="text-[13px] text-white/30">
              {query ? 'Try a different search term' : 'Add your first contact to get started'}
            </p>
          </div>
        ) : (
          grouped.map(([letter, group]) => (
            <div key={letter}>
              <div
                className="px-4 py-1.5"
                style={{ background: 'rgba(255,255,255,0.03)' }}
              >
                <span className="text-[12px] font-bold" style={{ color: '#4A9EE8' }}>
                  {letter}
                </span>
              </div>
              {group.map((contact, idx) => (
                <ContactRow
                  key={contact.id}
                  contact={contact}
                  isLast={idx === group.length - 1}
                  onMessage={onMessage}
                  onSelect={() => setSelectedContact(contact)}
                />
              ))}
            </div>
          ))
        )}
      </div>

      {/* Contact detail bottom sheet */}
      {selectedContact && (
        <ContactDetailSheet
          contact={selectedContact}
          onClose={() => setSelectedContact(null)}
          onMessage={() => { setSelectedContact(null); onMessage(); }}
        />
      )}

      {/* Add Contact Modal */}
      {modal === 'add' && (
        <ModalOverlay onClose={() => { setModal(null); setAddErrors({}); setAddForm({ name: '', phone: '', status: '' }); }}>
          <div className="flex items-center gap-3 mb-5">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #287FD8 0%, #1A5FAA 100%)' }}
            >
              <UserPlus size={18} className="text-white" />
            </div>
            <h2 className="text-[17px] font-bold text-white">Add New Contact</h2>
          </div>

          <div className="flex flex-col gap-3">
            <FormField
              label="Full Name"
              value={addForm.name}
              onChange={v => { setAddForm(f => ({ ...f, name: v })); setAddErrors(e => ({ ...e, name: undefined })); }}
              placeholder="e.g. Maria Santos"
              error={addErrors.name}
            />
            <FormField
              label="Phone Number"
              value={addForm.phone}
              onChange={v => { setAddForm(f => ({ ...f, phone: v })); setAddErrors(e => ({ ...e, phone: undefined })); }}
              placeholder="+63 912 345 6789"
              error={addErrors.phone}
              type="tel"
            />
            <FormField
              label="Status (optional)"
              value={addForm.status}
              onChange={v => setAddForm(f => ({ ...f, status: v }))}
              placeholder="Hey there!"
            />
          </div>

          <div className="flex gap-2.5 mt-5">
            <button
              onClick={() => { setModal(null); setAddErrors({}); setAddForm({ name: '', phone: '', status: '' }); }}
              className="flex-1 h-[44px] rounded-2xl text-[14px] font-semibold transition-all duration-150"
              style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)' }}
            >
              Cancel
            </button>
            <button
              onClick={handleAddContact}
              disabled={addLoading}
              className="flex-1 h-[44px] rounded-2xl text-[14px] font-semibold text-white transition-all duration-150 hover:opacity-90 active:scale-[0.98] flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #287FD8 0%, #1A5FAA 100%)' }}
            >
              {addLoading ? <Loader2 size={16} className="animate-spin" /> : null}
              Add Contact
            </button>
          </div>
        </ModalOverlay>
      )}

      {/* Import Contacts Modal */}
      {modal === 'import' && (
        <ModalOverlay onClose={() => { setModal(null); setImportSuccess(false); }}>
          <div className="flex items-center gap-3 mb-5">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)' }}
            >
              <Upload size={18} className="text-white" />
            </div>
            <h2 className="text-[17px] font-bold text-white">Import Contacts</h2>
          </div>

          {importSuccess ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(34,197,94,0.15)' }}
              >
                <Check size={28} className="text-green-400" />
              </div>
              <p className="text-[15px] font-semibold text-white">Contacts Imported!</p>
              <p className="text-[13px] text-white/40 text-center">Your contacts have been synced successfully.</p>
            </div>
          ) : (
            <>
              <p className="text-[14px] mb-4" style={{ color: 'rgba(255,255,255,0.55)' }}>
                Choose how you would like to import your contacts.
              </p>
              <div className="flex flex-col gap-2.5">
                {[
                  { label: 'From Phone Contacts', sub: 'Sync from your device', icon: <Phone size={18} /> },
                  { label: 'From CSV File', sub: 'Upload a .csv file', icon: <Upload size={18} /> },
                  { label: 'From QR Code', sub: 'Scan a contact QR', icon: <Users size={18} /> },
                ].map(opt => (
                  <button
                    key={opt.label}
                    onClick={handleImport}
                    className="flex items-center gap-3 px-4 py-3 rounded-2xl text-left transition-all duration-150 hover:opacity-80 active:scale-[0.98]"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(74,158,232,0.15)' }}
                    >
                      <span style={{ color: '#4A9EE8' }}>{opt.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] font-semibold text-white">{opt.label}</p>
                      <p className="text-[12px]" style={{ color: 'rgba(255,255,255,0.38)' }}>{opt.sub}</p>
                    </div>
                    <ChevronRight size={16} className="text-white/30 flex-shrink-0" />
                  </button>
                ))}
              </div>
              <button
                onClick={() => setModal(null)}
                className="w-full h-[44px] rounded-2xl text-[14px] font-semibold mt-4 transition-all duration-150"
                style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)' }}
              >
                Cancel
              </button>
            </>
          )}
        </ModalOverlay>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function QuickActionBtn({
  icon,
  label,
  gradient,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  gradient: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex-1 flex flex-col items-center gap-1.5 py-2.5 rounded-2xl transition-all duration-150 hover:opacity-85 active:scale-[0.97]"
      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center"
        style={{ background: gradient }}
      >
        <span className="text-white">{icon}</span>
      </div>
      <span className="text-[11px] font-medium" style={{ color: 'rgba(255,255,255,0.65)' }}>
        {label}
      </span>
    </button>
  );
}

function ContactRow({
  contact,
  isLast,
  onMessage,
  onSelect,
}: {
  contact: Contact;
  isLast: boolean;
  onMessage: () => void;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors duration-100 active:scale-[0.99]"
      style={{
        borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.04)',
        background: 'transparent',
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <div
          className="w-[50px] h-[50px] rounded-full flex items-center justify-center text-white font-bold text-[15px]"
          style={{ background: contact.avatarGradient }}
          aria-label={`${contact.name} avatar`}
        >
          {contact.initials}
        </div>
        {contact.isOnline && (
          <span
            className="absolute bottom-[1px] right-[1px] w-[12px] h-[12px] bg-green-400 rounded-full border-2"
            style={{ borderColor: '#101C29' }}
            aria-label="Online"
          />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-[15px] font-semibold text-white truncate">{contact.name}</p>
          {contact.isOnline && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ background: 'rgba(34,197,94,0.15)', color: '#4ade80' }}>
              online
            </span>
          )}
        </div>
        <p className="text-[12px] truncate" style={{ color: 'rgba(255,255,255,0.38)' }}>
          {contact.status || contact.phone || contact.email || ''}
        </p>
      </div>

      {/* Quick action buttons */}
      <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
        <button
          onClick={e => { e.stopPropagation(); }}
          className="flex items-center justify-center w-[36px] h-[36px] rounded-full transition-all duration-150"
          style={{ background: 'rgba(74,158,232,0.12)' }}
          aria-label={`Call ${contact.name}`}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(74,158,232,0.25)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(74,158,232,0.12)'; }}
        >
          <Phone size={16} style={{ color: '#4A9EE8' }} />
        </button>
        <button
          onClick={e => { e.stopPropagation(); onMessage(); }}
          className="flex items-center justify-center w-[36px] h-[36px] rounded-full transition-all duration-150"
          style={{ background: 'rgba(74,158,232,0.12)' }}
          aria-label={`Message ${contact.name}`}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(74,158,232,0.25)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(74,158,232,0.12)'; }}
        >
          <MessageCircle size={16} style={{ color: '#4A9EE8' }} />
        </button>
      </div>
    </button>
  );
}

function ContactDetailSheet({
  contact,
  onClose,
  onMessage,
}: {
  contact: Contact;
  onClose: () => void;
  onMessage: () => void;
}) {
  return (
    <>
      {/* Backdrop */}
      <div
        className="absolute inset-0 z-40"
        style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
        onClick={onClose}
      />
      {/* Sheet */}
      <div
        className="absolute bottom-0 left-0 right-0 z-50 rounded-t-3xl px-5 pt-5 pb-8"
        style={{ background: '#162233', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        {/* Drag handle */}
        <div className="w-10 h-1 rounded-full mx-auto mb-5" style={{ background: 'rgba(255,255,255,0.15)' }} />

        <div className="flex items-center gap-4 mb-5">
          <div
            className="w-[60px] h-[60px] rounded-full flex items-center justify-center text-white font-bold text-[20px] flex-shrink-0"
            style={{ background: contact.avatarGradient }}
          >
            {contact.initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[18px] font-bold text-white">{contact.name}</p>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
              {contact.phone || contact.email || ''}
            </p>
            {contact.status && (
              <p className="text-[12px] mt-0.5" style={{ color: '#4A9EE8' }}>{contact.status}</p>
            )}
          </div>
          {contact.isOnline && (
            <span className="text-[11px] font-medium px-2.5 py-1 rounded-full" style={{ background: 'rgba(34,197,94,0.15)', color: '#4ade80' }}>
              Online
            </span>
          )}
        </div>

        <div className="flex gap-3">
          <button
            className="flex-1 flex items-center justify-center gap-2 h-[46px] rounded-2xl text-[14px] font-semibold text-white transition-all duration-150 hover:opacity-90 active:scale-[0.98]"
            style={{ background: 'rgba(74,158,232,0.15)', border: '1px solid rgba(74,158,232,0.3)' }}
            aria-label={`Call ${contact.name}`}
          >
            <Phone size={17} style={{ color: '#4A9EE8' }} />
            <span style={{ color: '#4A9EE8' }}>Call</span>
          </button>
          <button
            onClick={onMessage}
            className="flex-1 flex items-center justify-center gap-2 h-[46px] rounded-2xl text-[14px] font-semibold text-white transition-all duration-150 hover:opacity-90 active:scale-[0.98]"
            style={{ background: 'linear-gradient(135deg, #287FD8 0%, #1A5FAA 100%)' }}
            aria-label={`Message ${contact.name}`}
          >
            <MessageCircle size={17} className="text-white" />
            <span>Message</span>
          </button>
        </div>
      </div>
    </>
  );
}

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="absolute inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
      <div
        className="w-full rounded-t-3xl px-5 pt-5 pb-8 max-w-lg"
        style={{ background: '#162233', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div className="w-10 h-1 rounded-full mx-auto mb-5" style={{ background: 'rgba(255,255,255,0.15)' }} />
        {children}
      </div>
    </div>
  );
}

function FormField({
  label,
  value,
  onChange,
  placeholder,
  error,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  error?: string;
  type?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[12px] font-semibold" style={{ color: 'rgba(255,255,255,0.5)' }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-[44px] rounded-2xl px-4 text-[14px] text-white outline-none transition-all duration-150"
        style={{
          background: 'rgba(255,255,255,0.07)',
          border: error ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(255,255,255,0.08)',
        }}
      />
      {error && <p className="text-[11px]" style={{ color: '#f87171' }}>{error}</p>}
    </div>
  );
}
